// periodCloseFromKey — the last calendar day of the period a KPI period key
// names. Frontend mirror of the backend function of the same name; the two are
// bound by the `periodClose` group in
// tests/shared/scheduleParityFixtures/kpiPeriod.json (ADR-015).
//
// This exists because the KPI headline and the KPI compliance report both
// attribute a result to the month its reporting period CLOSES in, so both
// stacks must agree on that date to the day — an ISO week starting in January
// and ending in February belongs to February on every surface.
//
// A key that does not match its cadence THROWS rather than guessing. Stored
// period strings are free text, so callers that read them decide what to do
// with an unparseable one; inventing a close date would silently place a
// result in the wrong month. All arithmetic is UTC.

import type { KpiFrequency } from "./period";

const PERIOD_KEY_RE: Record<KpiFrequency, RegExp> = {
  daily: /^(\d{4})-(\d{2})-(\d{2})$/u,
  weekly: /^(\d{4})-W(\d{2})$/u,
  monthly: /^(\d{4})-(\d{2})$/u,
  quarterly: /^(\d{4})-Q([1-4])$/u,
  annually: /^(\d{4})$/u,
};

const MS_PER_DAY = 86_400_000;

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

function formatUtc(date: Date): string {
  return `${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}-${pad2(
    date.getUTCDate(),
  )}`;
}

/** The last day of `month` (1-based) in `year`. Day zero of the next month is
 *  the last day of this one, which is leap-correct without a special case. */
function lastDayOfMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

export function periodCloseFromKey(
  periodKey: string,
  frequency: KpiFrequency,
): string {
  const match = PERIOD_KEY_RE[frequency].exec(periodKey);
  if (!match) {
    throw new Error(`Invalid ${frequency} period key: ${periodKey}`);
  }

  switch (frequency) {
    case "daily":
      return periodKey;
    case "monthly": {
      const year = Number(match[1]);
      const month = Number(match[2]);
      return `${year}-${pad2(month)}-${pad2(lastDayOfMonth(year, month))}`;
    }
    case "quarterly": {
      const year = Number(match[1]);
      const endMonth = Number(match[2]) * 3;
      return `${year}-${pad2(endMonth)}-${pad2(
        lastDayOfMonth(year, endMonth),
      )}`;
    }
    case "annually":
      return `${match[1]}-12-31`;
    case "weekly": {
      // ISO week 1 is the week containing 4 January. Week N's Monday is that
      // week's Monday plus 7 × (N − 1) days, and the period closes on the
      // Sunday six days later.
      const isoYear = Number(match[1]);
      const isoWeek = Number(match[2]);
      const jan4 = new Date(Date.UTC(isoYear, 0, 4));
      const jan4Dow = jan4.getUTCDay() || 7;
      const week1Monday = new Date(jan4.getTime() - (jan4Dow - 1) * MS_PER_DAY);
      const weekMonday = new Date(
        week1Monday.getTime() + (isoWeek - 1) * 7 * MS_PER_DAY,
      );
      return formatUtc(new Date(weekMonday.getTime() + 6 * MS_PER_DAY));
    }
  }
}
